import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface Kat {
    katID: number;
    katAdi: string;
    sira: number;
    aktif: boolean;
}

interface KatForm {
    katAdi: string;
    sira: number;
    aktif: boolean;
}

const Katlar: React.FC = () => {
    const { t } = useTranslation();
    const [katlar, setKatlar] = useState<Kat[]>([]);
    const [selectedKat, setSelectedKat] = useState<Kat | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<KatForm>({
        katAdi: '',
        sira: 0,
        aktif: true,
    });
    // Sıra girdisi metin olarak tutulur: negatif değer yazarken ara durum olan "-"
    // sayıya çevrilemediği için doğrudan sayı state'i girdiyi 0'a sıfırlıyordu.
    const [siraGirdi, setSiraGirdi] = useState('0');

    const fetchKatlar = async () => {
        try {
            const response = await axios.get('/api/Katlar');
            setKatlar(response.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    useEffect(() => { fetchKatlar(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedKat) {
                await axios.put(`/api/Katlar/${selectedKat.katID}`, formData);
                toast.success(t('msg.islemBasarili'));
            } else {
                await axios.post('/api/Katlar', formData);
                toast.success(t('msg.islemBasarili'));
            }
            fetchKatlar();
            handleCloseModal();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (kat: Kat) => {
        setSelectedKat(kat);
        setFormData({ katAdi: kat.katAdi, sira: kat.sira, aktif: kat.aktif });
        setSiraGirdi(String(kat.sira));
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedKat(null);
        setFormData({
            katAdi: '',
            sira: katlar.length > 0 ? Math.max(...katlar.map(k => k.sira)) + 1 : 1,
            aktif: true,
        });
    };

    const handleShowModal = () => {
        const yeniSira = katlar.length > 0 ? Math.max(...katlar.map(k => k.sira)) + 1 : 1;
        setFormData(prev => ({ ...prev, sira: yeniSira }));
        setSiraGirdi(String(yeniSira));
        setShowModal(true);
    };

    const sorted = [...katlar].sort((a, b) => a.sira - b.sira);

    return (
        <>
            <style>{`
                .kl-toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: #f9fafb; border-radius: 10px; border: 1px solid #e5e7eb; }
                .kl-toggle-label { font-size: 14px; font-weight: 500; color: #374151; }
                .kl-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
                .kl-switch input { opacity: 0; width: 0; height: 0; }
                .kl-switch-track { position: absolute; inset: 0; border-radius: 999px; background: #cbd5e1; cursor: pointer; transition: background 0.2s; }
                .kl-switch input:checked + .kl-switch-track { background: #22c55e; }
                .kl-switch-track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.18); transition: transform 0.2s; }
                .kl-switch input:checked + .kl-switch-track::after { transform: translateX(18px); }
                .kl-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.2); backdrop-filter: blur(2px); display: flex; justify-content: flex-end; z-index: 1050; }
                .kl-modal { background: #fff; border-radius: 16px 0 0 16px; width: 480px; max-width: 92vw; height: 100%; box-shadow: -8px 0 40px rgba(0,0,0,0.12); animation: klDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1); display: flex; flex-direction: column; overflow: hidden; }
                @keyframes klDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
                .kl-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: klSpin 0.7s linear infinite; }
                @keyframes klSpin { to { transform: rotate(360deg); } }

                /* ── DEAD CSS ZONE — replaced by Tailwind below ── */
                .kl-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 24px;
                    background: #ffffff;
                    min-height: unset;
                }

                /* ── Header ── */
                .kl-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .kl-header-left { display: flex; flex-direction: column; gap: 3px; }
                .kl-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .kl-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .kl-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .kl-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transform: translateY(-1px);
                }
                .kl-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Card ── */
                .kl-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                /* ── Table ── */
                .kl-table { width: 100%; border-collapse: collapse; }
                .kl-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .kl-table thead th {
                    padding: 13px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .kl-table thead th.kl-th-actions { text-align: right; }
                .kl-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .kl-table tbody tr:last-child { border-bottom: none; }
                .kl-table tbody tr.kl-row-hovered { background: #f0f5ff; }
                .kl-table tbody td {
                    padding: 15px 20px;
                    font-size: 14px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                /* ── Drag handle ── */
                .kl-drag-handle {
                    display: inline-flex;
                    align-items: center;
                    color: #cbd5e1;
                    cursor: grab;
                    padding: 2px 4px;
                    border-radius: 4px;
                    transition: color 0.15s;
                }
                .kl-row-hovered .kl-drag-handle { color: #94a3b8; }
                .kl-drag-handle:active { cursor: grabbing; }
                .kl-drag-handle svg { width: 14px; height: 14px; fill: currentColor; }

                /* Cell variants */
                .kl-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 54px; }
                .kl-cell-sira {
                    width: 90px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .kl-sira-num {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    background: #f1f5f9;
                    border-radius: 6px;
                    padding: 2px 8px;
                    min-width: 26px;
                    text-align: center;
                }
                .kl-cell-kat-adi { font-weight: 600; color: #0f172a; font-size: 15px; }
                .kl-cell-durum { width: 110px; }
                .kl-cell-actions { width: 90px; text-align: right; }

                /* Pill */
                .kl-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 11px 4px 8px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .kl-pill-aktif { background: #dcfce7; color: #15803d; }
                .kl-pill-pasif { background: #fee2e2; color: #b91c1c; }
                .kl-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                .kl-pill-aktif .kl-pill-dot { background: #22c55e; }
                .kl-pill-pasif .kl-pill-dot { background: #ef4444; }

                /* Ghost buttons */
                .kl-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .kl-row-hovered .kl-actions { opacity: 1; }
                .kl-ghost-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px; height: 34px;
                    border-radius: 8px;
                    border: 1.5px solid transparent;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .kl-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .kl-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .kl-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }

                /* Footer */
                .kl-footer {
                    padding: 13px 20px;
                    border-top: 1.5px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .kl-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .kl-count span { color: #475569; font-weight: 600; }

                /* Empty */
                .kl-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .kl-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .kl-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .kl-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.2);
                    backdrop-filter: blur(2px);
                    display: flex;
                    justify-content: flex-end;
                    z-index: 1050;
                }
                .kl-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px;
                    max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: klDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                @keyframes klDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                .kl-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-shrink: 0;
                }
                .kl-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .kl-modal-close {
                    width: 32px; height: 32px;
                    border-radius: 8px; border: none;
                    background: none; color: #9ca3af;
                    cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: all 0.15s;
                }
                .kl-modal-close:hover { background: #f3f4f6; color: #374151; }
                .kl-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .kl-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .kl-field label {
                    display: block;
                    font-size: 11px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.06em;
                    color: #6b7280; margin-bottom: 6px;
                }
                .kl-field input[type="text"],
                .kl-field input[type="number"] {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 9px;
                    font-size: 14px;
                    font-family: inherit;
                    color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                }
                .kl-field input:focus {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .kl-toggle-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 14px;
                    background: #f8fafc;
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                }
                .kl-toggle-label { font-size: 14px; font-weight: 500; color: #334155; }
                .kl-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
                .kl-switch input { opacity: 0; width: 0; height: 0; }
                .kl-switch-track {
                    position: absolute; inset: 0;
                    border-radius: 999px; background: #cbd5e1;
                    cursor: pointer; transition: background 0.2s;
                }
                .kl-switch input:checked + .kl-switch-track { background: #22c55e; }
                .kl-switch-track::after {
                    content: '';
                    position: absolute;
                    top: 3px; left: 3px;
                    width: 18px; height: 18px;
                    border-radius: 50%; background: #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.18);
                    transition: transform 0.2s;
                }
                .kl-switch input:checked + .kl-switch-track::after { transform: translateX(18px); }
                .kl-modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #f3f4f6;
                    display: flex; justify-content: flex-end; gap: 10px;
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(8px);
                    flex-shrink: 0;
                }
                .kl-btn-cancel {
                    padding: 9px 18px;
                    border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .kl-btn-cancel:hover { background: #f9fafb; }
                .kl-btn-save {
                    padding: 10px 20px;
                    border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .kl-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .kl-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .kl-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: klSpin 0.7s linear infinite;
                }
                @keyframes klSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: '24px', background: '#fff' }}>
                {/* ── Header ── */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Katlar</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>{t('ayarlar.kat.aciklama')}</p>
                    </div>
                    <button
                        onClick={handleShowModal}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { (e.target as HTMLElement).closest('button')!.style.background = '#a85505'; (e.target as HTMLElement).closest('button')!.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.target as HTMLElement).closest('button')!.style.background = '#cd6906'; (e.target as HTMLElement).closest('button')!.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Kat
                    </button>
                </div>

                {/* ── List Card ── */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                    {sorted.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>🏢</div>
                            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{t('msg.henuzKayitEklenmedi')}</p>
                        </div>
                    ) : (
                        sorted.map((kat, idx) => (
                            <div
                                key={kat.katID}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: idx < sorted.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.13s' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 28, height: 28, background: '#f3f4f6', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>{kat.sira}</div>
                                    <div>
                                        <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{kat.katAdi}</p>
                                        <p style={{ fontSize: 12, margin: '2px 0 0', color: kat.aktif ? '#059669' : '#e11d48', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: kat.aktif ? '#10b981' : '#f43f5e', display: 'inline-block' }} />
                                            {kat.aktif ? 'Aktif' : 'Pasif'}
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button
                                        title={t('btn.duzenle')}
                                        onClick={() => handleEdit(kat)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#2563eb', display: 'flex', transition: 'background 0.13s' }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                    >
                                        <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </button>
                                    <button
                                        title={t('btn.sil')}
                                        onClick={() => toast.info(t('msg.onayGerekli'))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#f43f5e', display: 'flex', transition: 'background 0.13s' }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#fff1f2')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                    >
                                        <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {sorted.length > 0 && (
                    <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
                        Toplam <strong style={{ color: '#374151' }}>{sorted.length}</strong> kat · Aktif: <strong style={{ color: '#374151' }}>{sorted.filter(k => k.aktif).length}</strong>
                    </p>
                )}
            </div>

            {showModal && (
                <div className="kl-overlay" onClick={handleCloseModal}>
                    <div className="kl-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>
                                {selectedKat ? t('ayarlar.kat.duzenle') : t('ayarlar.kat.yeni')}
                            </h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Kat Adı</label>
                                        <input
                                            type="text"
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            placeholder="Zemin Kat, 1. Kat..."
                                            value={formData.katAdi}
                                            onChange={e => setFormData({ ...formData, katAdi: e.target.value })}
                                            required maxLength={50} autoFocus
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Sıra No</label>
                                        <input
                                            type="number"
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            placeholder="0"
                                            value={siraGirdi}
                                            onChange={e => {
                                                setSiraGirdi(e.target.value);
                                                const n = parseInt(e.target.value, 10);
                                                if (!isNaN(n)) setFormData({ ...formData, sira: n });
                                            }}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="kl-toggle-row">
                                    <span className="kl-toggle-label">Durum — Aktif</span>
                                    <label className="kl-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.aktif}
                                            onChange={e => setFormData({ ...formData, aktif: e.target.checked })}
                                        />
                                        <span className="kl-switch-track" />
                                    </label>
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', transition: 'background 0.13s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >{t('btn.iptal')}</button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s', opacity: saving ? 0.75 : 1 }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                >
                                    {saving && <span className="kl-spinner" />}
                                    {selectedKat ? t('btn.kaydet') : t('btn.ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Katlar;